import { DayOfWeek, FormatPreset } from '@doss/prisma/types'
import dayjs, { Dayjs, ManipulateType } from 'dayjs'
import customParseFormat from 'dayjs/plugin/customParseFormat.js'
import 'dayjs/locale/de.js'
import 'dayjs/locale/es.js'
import 'dayjs/locale/fr.js'
import 'dayjs/locale/zh.js'
import localizedFormat from 'dayjs/plugin/localizedFormat.js'
import relativeTime from 'dayjs/plugin/relativeTime.js'
import timezone from 'dayjs/plugin/timezone.js'
import utc from 'dayjs/plugin/utc.js'
import weekOfYear from 'dayjs/plugin/weekOfYear.js'
import _ from 'lodash'
import { DossError } from './doss-error.js'
import { LocaleCode, LocaleCodeToDateFormatMap, LocaleCodeToTimeFormatMap } from './locales.js'
import { isTimeZone } from './time-zone.js'
import { FormatOptions, safeParseFormatOptions } from './types/format-options.js'

// Importing extend directly from dayjs causes esm/commonjs issues
dayjs.extend(customParseFormat)
dayjs.extend(utc)
dayjs.extend(relativeTime)
dayjs.extend(timezone)
dayjs.extend(localizedFormat)
dayjs.extend(weekOfYear)

/**
 * Common date formats to try when dayjs/Date can't parse a date string natively.
 * dayjs(string) delegates to new Date(string) which only reliably parses ISO 8601.
 * In Node.js environments with small-icu (e.g. Alpine Docker images), natural language
 * month names like "March 30, 2026" fail. These explicit formats use the customParseFormat
 * plugin which doesn't depend on ICU.
 */
const FALLBACK_DATE_FORMATS = [
  'MMMM D, YYYY',
  'MMMM DD, YYYY',
  'MMM D, YYYY',
  'MMM DD, YYYY',
  'D MMMM YYYY',
  'DD MMMM YYYY',
  'D MMM YYYY',
  'DD MMM YYYY',
]

/**
 * Parses a date string with dayjs, trying common natural language date formats as fallbacks
 * when the default parse fails (e.g. "March 30, 2026" in small-icu environments).
 * When a parseFormat is provided, it is passed to dayjs for strict parsing with no fallbacks.
 */
export function parseDateString(dateString: string | undefined, parseFormat?: string): Dayjs {
  const parsed = parseFormat ? dayjs(dateString, parseFormat) : dayjs(dateString)
  if (parsed.isValid() || parseFormat) {
    return parsed
  }
  // Try common natural language date formats via customParseFormat (ICU-independent)
  for (const fmt of FALLBACK_DATE_FORMATS) {
    const attempt = dayjs(dateString, fmt)
    if (attempt.isValid()) {
      return attempt
    }
  }
  return parsed
}

export function formatMilliseconds(value: unknown): string {
  if (typeof value !== 'number' || !Number.isFinite(value)) {
    return '-'
  }
  if (value < 1000) {
    return `${value}ms`
  }
  return `${(value / 1000).toFixed(2)}s`
}

/**
 * UTC variant of parseDateString.
 */
function parseDateStringUtc(dateString: string | undefined, parseFormat?: string): Dayjs {
  const parsed = parseFormat ? dayjs.utc(dateString, parseFormat) : dayjs.utc(dateString)
  if (parsed.isValid() || parseFormat) {
    return parsed
  }
  for (const fmt of FALLBACK_DATE_FORMATS) {
    const attempt = dayjs.utc(dateString, fmt)
    if (attempt.isValid()) {
      return attempt
    }
  }
  return parsed
}

/**
 * Return DayJS object for the current time in UTC
 */
export function toUtcDate(date?: Date | string): Dayjs {
  return dayjs.utc(date)
}

/**
 * ISO 8601 string format for UTC
 */
export const ISO_8601_DATE_TIME_FORMAT = 'YYYY-MM-DDTHH:mm:ss.SSS[Z]'
export const ISO_8601_DATE_FORMAT = 'YYYY-MM-DDTHH:mm:ss.SSS'
export const DATE_FILTER_STRING_FORMAT = 'YYYY-MM-DD'
export const DATE_TIME_FILTER_STRING_FORMAT = 'YYYY-MM-DDTHH:mm'

/**
 * Checks that the string input is in ISO 8601 format and is a valid date time
 */
export function isValidDateTimeString(dateString: string): boolean {
  return dayjs(dateString, ISO_8601_DATE_TIME_FORMAT, true).isValid()
}

/**
 * Checks that the string input is in ISO 8601 format and is a valid date
 */
export function isValidDateString(dateString: string): boolean {
  return dayjs(dateString, ISO_8601_DATE_FORMAT, true).isValid()
}

/**
 * Checks that the string input is the one for dates and the date is valid
 */
export function isValidDate(dateString: string): boolean {
  // Using strict check to validate that the date string format and parsed date are valid
  return dayjs(dateString, DATE_FILTER_STRING_FORMAT, true).isValid()
}

/**
 * Checks that the string input is one for date times and the date is valid
 */
export function isValidMuiDateTimeString(dateString: string): boolean {
  // Using strict check to validate that the date string format and parsed date are valid
  return dayjs(dateString, DATE_TIME_FILTER_STRING_FORMAT, true).isValid()
}

/**
 * This function is mainly used to convert a DATETIME to a DATE properly given the time zone context,
 * since we store DATE objects in the database as midnight UTC of the date as expected to be displayed.
 *
 * The time zone context is necessary because we create and store date-times in UTC in the system.
 * The browser will convert the UTC date-time to the local time zone when displaying it.
 * When we convert a date-time to a date, we need to know the date of that date-time in the local time zone.
 *
 * @param isoString a UTC date-time string in ISO 8601 format, e.g. '2024-07-02T00:25:00.000Z'
 * @param timeZoneContext time zone context of the date-time string, e.g. 'America/Los_Angeles',
 * @param parseFormat optional format string to parse the input date-time string
 * @returns a date object in UTC set to midnight of the date in the time zone context
 */
export function toMidnightUTCDateObjectWithTimeZoneContext(
  isoString: string | undefined,
  timeZoneContext: string,
  parseFormat?: string,
): Dayjs {
  // Parse the input ISO string and convert to the time zone provided in context
  const localTime = parseDateString(isoString, parseFormat).tz(timeZoneContext)

  // Extract the date in the provided time zone by formatting the local time to a date string
  const localDate = localTime.format('YYYY-MM-DD')

  // Create a new date object using the date string and set it to midnight UTC
  return dayjs.utc(localDate)
}

/** Mapping of format parts to their regex counterparts  */
const dateElementToRegexPatternMap: { [key: string]: string } = {
  YYYY: '(\\d{4})', // Four digits for the year
  YY: '(\\d{2})', // Two digits for the year
  DD: '\\d{2}', // Two digits for the day
  D: '\\d{1,2}', // One or two digits for the day (fallback for single day digit)
  MM: '\\d{2}', // Two digits for the month
  M: '\\d{1,2}', // One or two digits for the month (fallback for single month digit)
  HH: '\\d{2}', // Two digits for the hour (24-hour format)
  h: '\\d{1,2}', // One or two digits for the hour (12-hour format)
  mm: '\\d{2}', // Two digits for the minute
  ss: '\\d{2}', // Two digits for the second
  A: '(AM|PM)', // AM or PM for 12-hour format
}

/**
 * Generates a regular expression based on a date format string.
 *
 * Converts a date format (e.g., 'MM/DD/YYYY', 'DD.MM.YYYY') into a regular expression
 * for validating date strings. Commonly used in contexts like processing CSV uploads, where we
 * need to determine if it is a plain date string or ISO string, and
 * the date format is determined based on locale settings.
 *
 * Example formats:
 * - 'MM/DD/YYYY' for en-US locale
 * - 'DD.MM.YYYY' or 'D.M.YY' for de-DE locale
 * - 'DD/MM/YYYY HH:mm' for date-time formats
 */
export function getRegexFromDateFormat(format: string, matchEntireString?: boolean): RegExp {
  // Sort format keys by length (longest first) to avoid partial replacements
  const sortedKeys = _.orderBy(
    Object.keys(dateElementToRegexPatternMap),
    [(key): number => key.length],
    ['desc'],
  )

  // Escape special characters and replace format with regex
  let regexPattern = format.replace(/[.*+?^${}()|[\]\\]/g, '\\$&') // Escape special regex characters

  // Replace each format key in the sorted order
  sortedKeys.forEach((key) => {
    regexPattern = regexPattern.replace(new RegExp(key, 'g'), dateElementToRegexPatternMap[key])
  })

  // Add start (^) and end ($) anchors to ensure exact matching if required
  if (matchEntireString) {
    return new RegExp(`^${regexPattern}$`)
  }
  return new RegExp(regexPattern)
}

/**
 * Get the list of possible date formats for a given locale.
 */
export function getAllDateFormatsForLocale(locale: LocaleCode): string[] {
  const dateFormat = LocaleCodeToDateFormatMap[locale]
  return [
    dateFormat,
    dateFormat.replace('YYYY', 'YY'),
    dateFormat.replace('MM', 'M').replace('DD', 'D'),
    dateFormat.replace('MM', 'M').replace('DD', 'D').replace('YYYY', 'YY'),
  ]
}

/**
 * Check if the date string is a valid date string for any locale.
 */
export function isAnyValidDateString(dateString: string, locale?: LocaleCode): boolean {
  const locales = locale ? [locale] : Object.values(LocaleCode)

  for (const locale of locales) {
    const dateFormats = getAllDateFormatsForLocale(locale)
    for (const format of dateFormats) {
      try {
        if (dayjs(dateString, format).isValid()) {
          return true
        }
      } catch (error) {
        continue
      }
    }
  }

  return false
}

/**
 * Check if the date-time string is a valid date-time string for any locale.
 */
export function isAnyValidDateTimeString(dateTimeString: string, locale?: LocaleCode): boolean {
  const validDate = isAnyValidDateString(dateTimeString, locale)
  if (validDate) {
    return true
  }

  const locales = locale ? [locale] : Object.values(LocaleCode)

  for (const locale of locales) {
    const dateFormats = getAllDateFormatsForLocale(locale)

    for (const dateFormat of dateFormats) {
      const timeFormat = LocaleCodeToTimeFormatMap[locale]
      try {
        if (dayjs(dateTimeString, `${dateFormat} ${timeFormat}`).isValid()) {
          return true
        }
      } catch (error) {
        continue
      }
    }
  }

  return false
}

/* Date formats with separator-agnostic regex patterns */
const dateFormatsToRegexPatternsMap: { [key: string]: RegExp } = {
  // European date formats with periods
  'DD.MM.YYYY': /\d{2}[\\/.]\d{2}[\\/.]\d{4}/,
  'DD.MM.YY': /\d{2}[\\/.]\d{2}[\\/.]\d{2}/,
  'D.M.YYYY': /\d{1,2}[\\/.]\d{1,2}[\\/.]\d{4}/,
  'D.M.YY': /\d{1,2}[\\/.]\d{1,2}[\\/.]\d{2}/,
  // European date formats with slashes (British, French, etc.)
  'DD/MM/YYYY': /\d{2}[\\/.]\d{2}[\\/.]\d{4}/,
  'DD/MM/YY': /\d{2}[\\/.]\d{2}[\\/.]\d{2}/,
  'D/M/YYYY': /\d{1,2}[\\/.]\d{1,2}[\\/.]\d{4}/,
  'D/M/YY': /\d{1,2}[\\/.]\d{1,2}[\\/.]\d{2}/,
  // European date formats with dashes (Dutch)
  'DD-MM-YYYY': /\d{2}[\\/.-]\d{2}[\\/.-]\d{4}/,
  'DD-MM-YY': /\d{2}[\\/.-]\d{2}[\\/.-]\d{2}/,
  'D-M-YYYY': /\d{1,2}[\\/.-]\d{1,2}[\\/.-]\d{4}/,
  'D-M-YY': /\d{1,2}[\\/.-]\d{1,2}[\\/.-]\d{2}/,
  // American date formats
  'MM/DD/YYYY': /\d{2}[\\/.]\d{2}[\\/.]\d{4}/,
  'MM/DD/YY': /\d{2}[\\/.]\d{2}[\\/.]\d{2}/,
  'M/D/YYYY': /\d{1,2}[\\/.]\d{1,2}[\\/.]\d{4}/,
  'M/D/YY': /\d{1,2}[\\/.]\d{1,2}[\\/.]\d{2}/,
}

/**
 * Guess the date format of a date string based on the locale.
 * Only supports US and European date formats, will return null if the format is not recognized.
 */
export function guessDateFormatWithLocale(dateString: string, locale: LocaleCode): string | null {
  // Get all possible date formats for the locale
  const dateFormats = getAllDateFormatsForLocale(locale)
  // Iterate over the patterns to find a matching format
  for (const format of dateFormats) {
    const pattern = new RegExp(`^${dateFormatsToRegexPatternsMap[format].source}$`)
    if (pattern.test(dateString)) {
      return format
    }
  }
  // Return null if no format matches
  return null
}

/**
 * Guess the date-time format of a date-time string based on the locale.
 * Only supports US and European date time formats, will return null if the format is not recognized.
 */
export function guessDateTimeFormatWithLocale(
  dateString: string,
  locale: LocaleCode,
): string | null {
  // Get all possible date formats for the locale
  const dateFormats = getAllDateFormatsForLocale(locale)

  for (const format of dateFormats) {
    const datePattern = dateFormatsToRegexPatternsMap[format]
    const timeFormat = LocaleCodeToTimeFormatMap[locale]
    const timePattern = getRegexFromDateFormat(timeFormat)
    const pattern = new RegExp(`^${datePattern.source} ${timePattern.source}$`)
    if (pattern.test(dateString)) {
      return `${format} ${timeFormat}`
    }
  }
  return null
}

export type DateTimeConversionOptions = {
  setToMidnightUTC?: boolean
  timeZone?: string
  locale?: LocaleCode
  isDateString?: boolean // If true, the date string is a date, not a date-time
  customFormat?: string // Custom date format for parsing
}

/**
 * @param dateString
 * @param options DateTimeConversionOptions
 *
 * Convert date string to ISO 8601 dayjs object.
 *
 * If no options are provided, the dateString must be a date-time, not a date like "2023-10-31",
 * otherwise, it will infer "time" in the local time zone.
 * Given a date-time string with no options, there will be no material changes to the date itself,
 * which means the epoch time remains the same.
 *
 * If `setToMidnightUTC` is true, the time component of the date-time will be dropped.
 * If `timeZone` is provided, it will get the date in the local time context
 * and use that date to create a date object in midnight UTC.
 *
 */
export function toDateTimeObject(
  dateString: string | undefined,
  options?: DateTimeConversionOptions,
): Dayjs {
  const localeForParsing = options?.locale
  let parseFormat: string | undefined
  let guessedFormatFromLocale: string | undefined | null

  if (options?.timeZone && !isTimeZone(options?.timeZone)) {
    throw new DossError('Invalid time zone: %s', [options.timeZone])
  }

  // Determine the format to use for parsing the date string
  if (!_.isNil(options?.customFormat)) {
    // If a custom format is provided, use that format for parsing
    parseFormat = options.customFormat
  } else if (!_.isNil(localeForParsing) && !_.isNil(dateString)) {
    // If no custom format is provided but the locale is provided, try to guess the date or datetime format for the locale
    if (options?.isDateString === true) {
      guessedFormatFromLocale = guessDateFormatWithLocale(dateString, localeForParsing)
    } else {
      guessedFormatFromLocale = guessDateTimeFormatWithLocale(dateString, localeForParsing)
    }
    // If the date string finds a match with one of the possible formats in the provided locale, use that format for parsing
    // Otherwise, the date string is in a different format (e.g., ISO 8601)
    if (guessedFormatFromLocale !== null) {
      parseFormat = guessedFormatFromLocale
    }
  }

  if (options?.setToMidnightUTC === true) {
    // If the time zone is provided, convert the date string to the time zone and set it to midnight
    if (options?.timeZone !== undefined) {
      return toMidnightUTCDateObjectWithTimeZoneContext(dateString, options.timeZone, parseFormat)
    } else {
      // If the time zone is not provided, set the date string to midnight UTC
      return parseDateStringUtc(dateString, parseFormat).startOf('day')
    }
  } else if (!_.isNil(parseFormat) && !_.isNil(options?.timeZone)) {
    // Parse the date string as if it's naive local time
    const dateValue = parseDateString(dateString, parseFormat)

    // Convert this naive local time to the target timezone
    return dayjs.tz(dateValue.format('YYYY-MM-DDTHH:mm:ss'), options.timeZone)
  } else if (!_.isNil(options?.timeZone)) {
    // Parse the date string as if it's naive local time
    const dateValue = parseDateString(dateString)

    // Convert this naive local time to the target timezone
    return dayjs.tz(dateValue.format('YYYY-MM-DDTHH:mm:ss'), options.timeZone)
  } else {
    return parseDateString(dateString, parseFormat).utc()
  }
}

/**
 *
 * @param dateString
 * @param options DateTimeConversionOptions
 *
 * Convert date string to ISO 8601 string format.
 *
 * If no options are provided, the dateString must be a date-time, not a date like "2023-10-31",
 * otherwise, it will infer "time" in the local time zone.
 * Given a date-time string with no options, there will be no material changes to the date itself,
 * which means the epoch time remains the same.
 *
 * If `setToMidnightUTC` is true, the time component of the date-time will be dropped.
 * If `time zone` is provided, it will get the date in the local time context
 * and use that date to create a date object in midnight UTC.
 *
 */
export function toDateTimeString(
  dateString: string | undefined,
  options?: DateTimeConversionOptions,
): string {
  const date = toDateTimeObject(dateString, options)
  if (!date.isValid()) {
    throw new DossError('Invalid date value: %s', [JSON.stringify(dateString)])
  }
  const dateStringResult = date.toISOString()

  if (options?.setToMidnightUTC) {
    return dateStringResult.replace('Z', '')
  } else {
    return dateStringResult
  }
}

export interface FormatDateOptions {
  format?: string
  localeCode?: LocaleCode
  timeZone?: string
}

/**
 * Applies the format to the input date string. Returns an empty string if the input is null,
 * undefined, or an invalid date.
 *
 * Handles both DATE strings and DATE_TIME strings appropriately.
 */
export function formatDate(
  dateString: string | undefined | null,
  options: FormatDateOptions = {},
): string {
  if (!dateString) {
    return ''
  }

  try {
    const { format = 'L', localeCode = LocaleCode.EN_US, timeZone } = options

    const locale = localeCode.toString().split('-')[0]

    let date: dayjs.Dayjs

    const isUTC = dateString.endsWith('Z')
    if (isUTC) {
      // UTC date strings must be converted to the appropriate tz
      date = dayjs.utc(dateString)
      if (timeZone) {
        date = date.tz(timeZone)
      }
    } else {
      // Date strings with no time or with midnight time (no 'Z') represent a Date in the correct tz
      date = timeZone ? dayjs.tz(dateString, timeZone) : dayjs(dateString)
    }

    if (!date.isValid()) {
      return ''
    }

    return date.locale(locale).format(format)
  } catch (error) {
    console.error('Error formatting date', error)
    return dateString
  }
}

/**
 * Applies the format to the input date string. Returns an empty string if the input is null,
 * undefined, or an invalid date time.
 */
export function formatDateTime(
  dateTimeString: string | number | Date | undefined | null,
  timeZone: string | undefined,
  format: string,
): string {
  if (!dateTimeString) {
    return ''
  }
  if (timeZone && !isTimeZone(timeZone)) {
    throw new DossError('Invalid time zone: %s', [timeZone])
  }

  const dateTime = dayjs(dateTimeString)
  if (!dateTime.isValid()) {
    return ''
  }
  return timeZone ? dateTime.tz(timeZone).format(format) : dateTime.format(format)
}

/**
 * Applies the locale-default format to the input date-time string.
 * Returns an empty string if the input is null, undefined, or an invalid date time.
 * In default locale (en), the format is 'MM/DD/YYYY h:mm A'
 */
export function formatDateTimeByLocale(
  dateTimeString: string | number | Dayjs | undefined | null,
  localeCode: LocaleCode = LocaleCode.EN_US,
  timeZone?: string | undefined,
  showTimeZone = false,
): string {
  if (!dateTimeString) {
    return ''
  }
  if (timeZone && !isTimeZone(timeZone)) {
    throw new DossError('Invalid time zone: %s', [timeZone])
  }

  // All LocaleCodes are in the format of 'en-US'
  // so we need to split the string to get the language code
  const locale = localeCode.toString().split('-')[0]
  const dateTime = dayjs(dateTimeString).locale(locale)
  if (!dateTime.isValid()) {
    return ''
  }

  // L = MM/DD/YYYY in English locale
  // LT = h:mm A in English locale
  const zonedDateTime = timeZone
    ? (wallClockAsUtc(dateTime.valueOf(), timeZone)?.locale(locale) ?? dateTime.tz(timeZone))
    : dateTime.tz(timeZone)
  const formatted = zonedDateTime.format('L LT')
  if (!showTimeZone) {
    return formatted
  }
  // Guessing the local zone and naming it both build an Intl formatter, so only when shown.
  const tzAbbreviation = getTimezoneAbbreviation(
    zonedDateTime.toDate(),
    timeZone ?? dayjs.tz.guess(),
  )
  return `${formatted} (${tzAbbreviation})`
}

/**
 * Formats a dayjs datetime object using the specified format string.
 * Additionally, appends the provided timezone's abbreviation to the end of the formatted date
 *
 * @param dateTime
 * @param format
 * @param timezone
 */
export function formatDateWithTimezone(
  dateTime: Dayjs,
  format: string,
  timezone?: string | undefined,
): string {
  if (timezone && !isTimeZone(timezone)) {
    throw new DossError('Invalid time zone: %s', [timezone])
  }
  if (!timezone) {
    timezone = dayjs.tz.guess()
  }

  const formattedTime = dayjs(dateTime).tz(timezone).format(format)
  const tzAbbrv = getTimezoneAbbreviation(dateTime.toDate(), timezone)
  return `${formattedTime} (${tzAbbrv})`
}

/** Intl formatters that read an instant as wall-clock fields in a zone, keyed by zone. */
const wallClockFormatCache = new Map<string, Intl.DateTimeFormat>()

/** Wall-clock fields carry no era, so this is the first instant they name unambiguously. */
const WALL_CLOCK_MIN_MS = Date.UTC(1000, 0, 1)

/**
 * The wall-clock reading of `ms` in `timeZone`, as a UTC-mode Dayjs holding those fields, so a
 * format with no zone tokens renders it directly. dayjs's `tz()` builds an uncached Intl
 * formatter, prints the instant, and re-parses the print; this is one cached `formatToParts`.
 * Returns null for anything the fields cannot name (before year 1000, or a part Intl left out),
 * and the caller falls back to `tz()`.
 */
function wallClockAsUtc(ms: number, timeZone: string): Dayjs | null {
  if (!(ms >= WALL_CLOCK_MIN_MS)) {
    return null
  }
  let fmt = wallClockFormatCache.get(timeZone)
  if (!fmt) {
    fmt = new Intl.DateTimeFormat('en-US', {
      timeZone,
      hourCycle: 'h23',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    })
    wallClockFormatCache.set(timeZone, fmt)
  }
  const fields: Partial<Record<Intl.DateTimeFormatPartTypes, number>> = {}
  for (const part of fmt.formatToParts(new Date(ms))) {
    if (part.type !== 'literal') {
      fields[part.type] = Number(part.value)
    }
  }
  const { year, month, day, hour, minute, second } = fields
  if (
    year === undefined ||
    month === undefined ||
    day === undefined ||
    hour === undefined ||
    minute === undefined ||
    second === undefined
  ) {
    return null
  }
  const wallClock = new Date(0)
  wallClock.setUTCFullYear(year, month - 1, day)
  // Some ICU builds print midnight as 24 under h23.
  wallClock.setUTCHours(hour === 24 ? 0 : hour, minute, second, ms % 1000)
  return dayjs.utc(wallClock)
}

/**
 * Cache for Intl.DateTimeFormat instances to avoid expensive re-instantiation.
 * Key format: timezone string (e.g., 'America/Los_Angeles')
 *
 * Intl.DateTimeFormat instantiation is expensive (10-100ms), and since this is called on every cell
 * render in tables, caching provides significant performance gains to prevent event loop lag.
 */
const dateTimeFormatCache = new Map<string, Intl.DateTimeFormat>()

function getTimezoneAbbreviation(date: Date, tz: string): string {
  // Check cache first
  let fmt = dateTimeFormatCache.get(tz)

  if (!fmt) {
    // Cache miss - create and store formatter
    fmt = new Intl.DateTimeFormat('en-US', {
      timeZone: tz,
      timeZoneName: 'short',
    })
    dateTimeFormatCache.set(tz, fmt)
  }

  const parts = fmt.formatToParts(date)
  const abbrv = parts.find((p) => p.type === 'timeZoneName')?.value
  return abbrv || ''
}

/**
 * Returns the difference in days between two ISO 8601 date strings
 * Not absolute and can return negatives
 * First date subtracts the second date (date1 - date2) - negative if first date is earlier
 */
export function getDaysDifference(date1: string, date2: string): number {
  return dayjs(date1).diff(dayjs(date2), 'day')
}

/**
 * Returns today's date set to midnight UTC as an ISO 8601 string
 */
export function utcMidnightToISOString(): string {
  return dayJsToMidnightUTCDateTimeString(dayjs())
}

/**
 * Returns Dayjs at midnight at UTC
 *
 * This function should only be used on web because it uses the time zone of the runtime environment.
 * If we use it as a part of workflow, it will use the time zone of the server, which would be incorrect.
 *
 * "2024-07-02T00:25:00.000Z" -> "2024-07-01T00:00:00.000Z" if you run this on local machine which is in PDT
 * This behavior will be different if you run this on a server in another time zone.
 */
export function dayJsToMidnightUTCDateTimeString(date: Dayjs): string {
  // utc(keepLocalTime: boolean) - Keep the local time and set the time zone to UTC
  // Passing true will change the time zone without changing the current time so the date won't
  // change if the local date and UTC date are different
  // This means that the time will be the same as the local time but the time zone will be UTC
  return date.utc(true).startOf('day').toISOString().replace('Z', '')
}

/**
 * Checks that the string input for DATE type is set to midnight UTC
 */
export function isMidnightUTC(dateString: string): boolean {
  const day = dayjs.utc(dateString)
  const isMidnight = day.hour() === 0 && day.minute() === 0 && day.second() === 0

  return day.isUTC() && isMidnight
}

/**
 * Returns the number of days between the date and today
 */
export const daysAgo = (day: string): string => {
  const date = dayjs(day)
  const today = dayjs()
  const diff = today.diff(date, 'day')
  if (diff === 0) {
    return 'Today'
  } else if (diff === 1) {
    return 'Yesterday'
  } else {
    return `${diff} days ago`
  }
}

/**
 * Gets the week number of the year for the given date
 */
export function getWeekNumber(dateString: string | number | undefined | null): {
  week: number
  year: number
} {
  if (!dateString) {
    return { week: 0, year: 0 }
  }

  const date = dayjs.utc(dateString)
  if (!date.isValid()) {
    return { week: 0, year: 0 }
  }

  return { week: date.week(), year: date.year() }
}

export type TimeValue = {
  hour: number
  minute: number
}

/**
 * Convert 24-hour format to 12-hour display
 */
export function get12HourDisplay(hour: number): string {
  if (hour === 0 || hour === 12) {
    return '12'
  }

  if (hour > 12) {
    return (hour - 12).toString().padStart(2, '0')
  }
  return hour.toString().padStart(2, '0')
}

// Core time unit definitions
export const UNITS = {
  THIS: ['week', 'month', 'year'] as const,
  TIME: ['minutes', 'hours', 'days', 'weeks', 'months', 'years'] as const,
  RELATIVE_OPERATORS: ['this', 'last', 'notInTheLast', 'inTheNext'] as const,
  // The current period a relative range can exclude
  EXCLUDE_CURRENT: ['day', 'week', 'month', 'year'] as const,
  RANGE_OPERATORS: ['between', 'notBetween'] as const,
  STANDARD_OPERATORS: [
    'is',
    'not',
    'after',
    'onOrAfter',
    'before',
    'onOrBefore',
    'isEmpty',
    'isNotEmpty',
    'isOn', // Custom operator for date-time fields only
    'notOn', // Custom operator for date-time fields only
  ] as const,
} as const

// Type definitions
export type TimeUnit = (typeof UNITS.TIME)[number]
export type ThisTimeUnit = (typeof UNITS.THIS)[number]
export type RelativeDateOperator = (typeof UNITS.RELATIVE_OPERATORS)[number]
export type ExcludeCurrentUnit = (typeof UNITS.EXCLUDE_CURRENT)[number]
export type DateRangeOperator = (typeof UNITS.RANGE_OPERATORS)[number]
export type StandardDateOperator = (typeof UNITS.STANDARD_OPERATORS)[number]

/**
 * Type guard to check if a string is a relative date range operator
 * (e.g., 'this', 'last', 'notInTheLast', 'inTheNext')
 */
export function isRelativeDateRangeOperator(value: string): value is RelativeDateOperator {
  return UNITS.RELATIVE_OPERATORS.includes(value as RelativeDateOperator)
}

/**
 * Type guard to check if a string is a date range operator
 * (e.g., 'between', 'notBetween')
 */
export function isDateRangeOperator(value: string): value is DateRangeOperator {
  return UNITS.RANGE_OPERATORS.includes(value as DateRangeOperator)
}

/**
 * Type guard to check if a string is a standard date operator
 * (e.g., 'is', 'not', 'after', etc.)
 */
export function isStandardDateOperator(value: string): value is StandardDateOperator {
  return UNITS.STANDARD_OPERATORS.includes(value as StandardDateOperator)
}

export type DateRange = {
  from?: Dayjs
  to?: Dayjs
}

// Single source of truth for unit mappings
export const UNIT_MAPPINGS = {
  TIME: {
    minutes: 'minute',
    hours: 'hour',
    days: 'day',
    weeks: 'week',
    months: 'month',
    years: 'year',
  } as const satisfies Record<TimeUnit, ManipulateType>,

  THIS: {
    week: 'week',
    month: 'month',
    year: 'year',
  } as const satisfies Record<ThisTimeUnit, ManipulateType>,
} as const

// Base interface for all dynamic values
// Org settings that change where a relative range's periods start
export type RelativeRangeOptions = {
  // 0 (Sunday) through 6 (Saturday); defaults to Sunday
  startOfWeekDay?: number
}

interface DynamicValueBase {
  calculateRange(now: dayjs.Dayjs, options?: RelativeRangeOptions): DateRange
}

export const DAY_OF_WEEK_TO_NUMBER: Record<DayOfWeek, number> = {
  [DayOfWeek.Sunday]: 0,
  [DayOfWeek.Monday]: 1,
  [DayOfWeek.Tuesday]: 2,
  [DayOfWeek.Wednesday]: 3,
  [DayOfWeek.Thursday]: 4,
  [DayOfWeek.Friday]: 5,
  [DayOfWeek.Saturday]: 6,
}

/**
 * Start of the week containing `date`, for a week that starts on `startOfWeekAsNumber` (0 = Sunday)
 */
export function getStartOfWeekDayJsTimestamp(date: Dayjs, startOfWeekAsNumber = 0): Dayjs {
  const dayOfWeek = date.day()
  const daysToSubtract = (dayOfWeek - startOfWeekAsNumber + 7) % 7
  return date.subtract(daysToSubtract, 'day').startOf('day')
}

/**
 * Start of the day, week, month or year containing `date`. Weeks start on the org's start of week.
 */
export function startOfExcludedPeriod(
  date: Dayjs,
  period: ExcludeCurrentUnit,
  options?: RelativeRangeOptions,
): Dayjs {
  return period === 'week'
    ? getStartOfWeekDayJsTimestamp(date, options?.startOfWeekDay)
    : date.startOf(period)
}

export class ThisPeriod implements DynamicValueBase {
  readonly type = 'this'
  constructor(readonly unit: ThisTimeUnit) {}

  calculateRange(now: dayjs.Dayjs, options?: RelativeRangeOptions): DateRange {
    if (this.unit === 'week') {
      // Weeks start on the org's start of week, as the filter does, not on dayjs's Sunday
      const from = getStartOfWeekDayJsTimestamp(now, options?.startOfWeekDay)
      return {
        from,
        to: from.add(6, 'day').endOf('day'),
      }
    }
    const dayjsUnit = UNIT_MAPPINGS.THIS[this.unit]
    return {
      from: now.startOf(dayjsUnit),
      to: now.endOf(dayjsUnit),
    }
  }
}

export function isSubDayTimeUnit(unit: TimeUnit): boolean {
  return unit === 'minutes' || unit === 'hours'
}

/**
 * Whether a relative range can exclude the current day, week, month or year:
 * `last` and `inTheNext`, and only for day+ units
 */
export function canExcludeCurrent(operator: string, unit: string): boolean {
  const isDayOrLonger =
    (UNITS.TIME as readonly string[]).includes(unit) && !isSubDayTimeUnit(unit as TimeUnit)
  return (operator === 'last' || operator === 'inTheNext') && isDayOrLonger
}

/**
 * English wording for an excluded period, matching the translated picker strings:
 * "excluding today" or "excluding this week"
 */
export function describeExcludeCurrent(unit: ExcludeCurrentUnit): string {
  return unit === 'day' ? 'excluding today' : `excluding this ${unit}`
}

export class RelativePeriod implements DynamicValueBase {
  readonly type = 'relative'
  constructor(
    readonly operator: Exclude<RelativeDateOperator, 'this'>,
    readonly value: number,
    readonly unit: TimeUnit,
    // Shifts the range out of the current day, week (from the org's start of week), month or year,
    // keeping its length:
    // "last 7 days, excluding the current week" is the 7 days ending the day before this week starts,
    // "in the next 7 days, excluding today" the 7 days after today
    readonly excludeCurrent?: ExcludeCurrentUnit,
  ) {}

  calculateRange(now: dayjs.Dayjs, options?: RelativeRangeOptions): DateRange {
    const dayjsUnit = UNIT_MAPPINGS.TIME[this.unit]
    const offset = this.value
    // Day+ ranges count today as their first day, so they're 1 day shorter at the far end.
    // Sub-day ranges are exact, as the filter evaluates them: "last 3 hours" starts 3 hours ago.
    const isSubDayUnit = isSubDayTimeUnit(this.unit)

    switch (this.operator) {
      case 'last': {
        if (this.excludeCurrent && canExcludeCurrent(this.operator, this.unit)) {
          // Ends the day before the current period. Count back from the period's start, not that day,
          // so months stay whole: 3 months before Dec 1 is Sep 1, not Aug 31.
          const periodStart = startOfExcludedPeriod(now, this.excludeCurrent, options)
          return {
            from: periodStart.subtract(offset, dayjsUnit),
            to: periodStart.subtract(1, 'day').endOf('day'),
          }
        }
        return {
          from: isSubDayUnit
            ? now.subtract(offset, dayjsUnit)
            : now.subtract(offset, dayjsUnit).add(1, 'day'),
          to: now,
        }
      }
      case 'notInTheLast':
        return {
          from: dayjs(0),
          to: now.subtract(offset, dayjsUnit),
        }
      case 'inTheNext': {
        if (this.excludeCurrent && canExcludeCurrent(this.operator, this.unit)) {
          // Starts with the next period
          const nextPeriod = startOfExcludedPeriod(
            now.add(1, this.excludeCurrent),
            this.excludeCurrent,
            options,
          )
          return {
            from: nextPeriod,
            to: nextPeriod.add(offset, dayjsUnit).subtract(1, 'day').endOf('day'),
          }
        }
        return {
          from: now,
          to: isSubDayUnit
            ? now.add(offset, dayjsUnit)
            : now.add(offset, dayjsUnit).subtract(1, 'day'),
        }
      }
    }
  }

  // Helper to get singular form of the unit
  getSingularUnit(): string {
    return UNIT_MAPPINGS.TIME[this.unit]
  }
}

export type DynamicValue = ThisPeriod | RelativePeriod

export const createDynamicValue = {
  this: (unit: ThisTimeUnit): ThisPeriod => new ThisPeriod(unit),
  relative: (
    operator: Exclude<RelativeDateOperator, 'this'>,
    value: number,
    unit: TimeUnit,
    excludeCurrent?: ExcludeCurrentUnit,
  ): RelativePeriod => new RelativePeriod(operator, value, unit, excludeCurrent),
}

/**
 * Calculates a date range based on the dynamic value.
 */
export function calculateDateRange(
  value: DynamicValue | undefined,
  options?: RelativeRangeOptions,
): DateRange {
  if (!value) {
    return {
      from: undefined,
      to: undefined,
    }
  }

  return value.calculateRange(dayjs(), options)
}

// Export constants for use in component
export const DATE_CONSTANTS = {
  UNITS,
  UNIT_MAPPINGS,
} as const

/**
 * Transforms a Xero-formatted date string (e.g., "/Date(1234567890000+0000)/") into a UTC date-time string
 * in the format "YYYY-MM-DD HH:mm:ss". If the input is not a valid Xero date string, returns the original value.
 *
 * @param value - The value to return if the original value is not a valid Xero date string.
 * @param originalValue - The original value, expected to be a Xero-formatted date string.
 * @returns The transformed UTC date-time string, or the original value if transformation is not possible.
 */
export function xeroDateTransform(value: unknown, originalValue: unknown): string | undefined {
  if (typeof originalValue === 'string') {
    const match = /Date\((\d+)([+-]\d{4})?\)/.exec(originalValue)
    if (match) {
      const timestamp = parseInt(match[1], 10)
      return dayjs.utc(timestamp).format('YYYY-MM-DD HH:mm:ss')
    }
  }
  return value as string | undefined
}

// Converts a date to the format YYYYMMDD
export function dateToYYYYMMDD(originalValue: unknown): string | undefined {
  if (!originalValue) {
    return undefined
  }

  const date = new Date(originalValue as string | number | Date)
  if (isNaN(date.getTime())) {
    return undefined
  }

  // Normalize to UTC so formatting is deterministic across time zones
  const year = date.getUTCFullYear()
  const month = String(date.getUTCMonth() + 1).padStart(2, '0')
  const day = String(date.getUTCDate()).padStart(2, '0')

  return `${year}${month}${day}`
}

type JSONValue = unknown | string | number | boolean | null | JSONObject | JSONValue[]

interface JSONObject {
  [key: string]: JSONValue
}

/**
 * Recursively converts all Unix timestamps (in seconds)
 * within a JSON object or array into ISO 8601 strings.
 * Converts 0 values to null (representing missing/not-yet-occurred timestamps).
 */
export function convertUnixToISO<T extends JSONValue>(input: T): T {
  if (Array.isArray(input)) {
    return input.map(convertUnixToISO) as T
  }

  if (input && typeof input === 'object') {
    const result: JSONObject = {}
    for (const [key, value] of Object.entries(input)) {
      result[key] = convertUnixToISO(value)
    }
    return result as T
  }

  // Convert 0 to null for timestamp fields (represents missing/not-yet-occurred timestamps)
  if (input === 0) {
    return null as T
  }

  if (
    typeof input === 'number' &&
    Number.isFinite(input) &&
    input > 1000000000 && // roughly > year 2001
    input < 99999999999 // roughly < year 5138
  ) {
    return new Date(input * 1000).toISOString() as T
  }

  return input
}

/**
 * Converts a string value to UTC ISO 8601 format.
 *
 * @param value - The input value to convert. If not a string, it will be returned unchanged.
 * @returns The UTC ISO string if the input is a valid date string, otherwise returns the original value unchanged.
 *
 * @example
 * ```typescript
 * toUtcIso('2023-12-01T10:30:00') // Returns: '2023-12-01T10:30:00.000Z'
 * toUtcIso('invalid-date') // Returns: 'invalid-date'
 * toUtcIso(123) // Returns: 123
 * ```
 */
export function toUtcIso(value: unknown): unknown {
  if (typeof value !== 'string') {
    return value
  }
  const d = dayjs(value)
  if (!d.isValid()) {
    return value
  }
  return d.utc().toISOString()
}

const MIN_REASONABLE_EPOCH_MS = Date.UTC(2000, 0, 1) // 946684800000
const MAX_REASONABLE_EPOCH_MS = Date.UTC(2100, 0, 1) // 4102444800000

export function looksLikeEpochMs(value: number): boolean {
  return (
    Number.isInteger(value) && value >= MIN_REASONABLE_EPOCH_MS && value <= MAX_REASONABLE_EPOCH_MS
  )
}

/**
 * Recursively converts Unix timestamps in milliseconds to ISO 8601 strings within a JSON-like structure.
 *
 * - If the input is an array, each element is processed recursively.
 * - If the input is an object, each property value is processed recursively.
 * - If the input is `0`, it is converted to `null` (representing a missing timestamp).
 * - If the input is a finite number within the range of plausible Unix ms timestamps,
 *   it is converted to an ISO string.
 * - All other values are returned unchanged.
 *
 * @param input - The JSON-like value to process (can be a primitive, array, or object).
 * @returns The processed value with Unix ms timestamps converted to ISO strings.
 */
export function deepConvertUnixMsToISO<T extends JSONValue>(input: T): T {
  if (Array.isArray(input)) {
    return input.map(deepConvertUnixMsToISO) as T
  }

  if (input && typeof input === 'object') {
    const result: JSONObject = {}
    for (const [key, value] of Object.entries(input)) {
      result[key] = deepConvertUnixMsToISO(value)
    }
    return result as T
  }

  // Convert 0 to null (missing timestamp)
  if (input === 0) {
    return null as T
  }

  if (
    typeof input === 'number' &&
    Number.isFinite(input) &&
    input > 1_000_000_000_000 && // ~2001 in ms
    input < 10_000_000_000_000 // far future safeguard
  ) {
    return new Date(input).toISOString() as T
  }

  return input
}

export function getFormattedDateValue(
  value: string | number | Date,
  formatOptions: FormatOptions<typeof FormatPreset.DATE_CUSTOM>,
): string {
  return dayjs(value).format(formatOptions.pattern)
}

type DateColumnFormat = {
  formatOptions?: unknown
  formatPreset?: FormatPreset | null
  isDateTime?: boolean
  timeZone?: string
}

export function formatDateColumnValue(
  value: unknown,
  columnFormat: DateColumnFormat,
  locale: LocaleCode,
): string {
  if (
    !value ||
    (typeof value !== 'string' && typeof value !== 'number' && !(value instanceof Date))
  ) {
    return ''
  }

  if (columnFormat.formatPreset === FormatPreset.DATE_CUSTOM) {
    const parsed = safeParseFormatOptions(FormatPreset.DATE_CUSTOM, columnFormat.formatOptions)
    if (parsed.success && parsed.data.pattern !== '') {
      if (columnFormat.isDateTime) {
        return formatDateTime(value, columnFormat.timeZone, parsed.data.pattern)
      }
      return getFormattedDateValue(value, parsed.data)
    }
  }

  if (columnFormat.isDateTime) {
    return formatDateTimeByLocale(
      value instanceof Date ? value.toISOString() : value,
      locale,
      columnFormat.timeZone,
    )
  }
  return formatDate(value instanceof Date ? value.toISOString() : String(value), {
    localeCode: locale,
  })
}

/**
 * Normalizes a time input to the 24-hour "HH:mm" format. Accepts a bare time ("9:00 PM"), an
 * already-24-hour time ("21:00"), or a full datetime string ("08/20/2026 9:00 PM") and keeps only
 * the time. Returns the original trimmed value when no time can be parsed, and '' for empty/nullish
 * input.
 */
export function formatTimeToHHmm(value: string | null | undefined): string {
  if (value === null || value === undefined) {
    return ''
  }
  const trimmed = value.trim()
  if (trimmed === '') {
    return ''
  }
  // Grab the first H:mm(:ss)? token and an optional AM/PM meridiem; the date portion (if any) has
  // no colon so it is skipped.
  const match = trimmed.match(/(\d{1,2}):(\d{2})(?::\d{2})?\s*([AaPp][Mm])?/)
  if (match === null) {
    return trimmed
  }
  const [, rawHours, minutes, meridiem] = match
  let hours = Number(rawHours)
  if (Number.isNaN(hours) || hours > 23 || Number(minutes) > 59) {
    return trimmed
  }
  if (meridiem !== undefined) {
    const isPm = meridiem.toLowerCase() === 'pm'
    if (isPm && hours < 12) {
      hours += 12
    } else if (!isPm && hours === 12) {
      hours = 0
    }
  }
  return `${hours.toString().padStart(2, '0')}:${minutes}`
}
